<template>
  <page
    :name="domain"
    :title="title"
  >
    <template #breadcrumbs>
      <pf-breadcrumb-item :to="{ name: 'services' }">{{ $t('My Hosting') }}</pf-breadcrumb-item>
    </template>

    <pf-page-section >
      <pf-empty-state v-if="servicesState.updating">
        <pf-empty-state-header :title="$t('Caricamento dei servizi')" heading-level="h4">
          <template #icon>
            <pf-empty-state-icon>
              <pf-spinner />
            </pf-empty-state-icon>
          </template>
        </pf-empty-state-header>
      </pf-empty-state>

      <pf-empty-state v-else-if="services && !services.pannello && !services.posta && !services.pecs && !services.dns && services.redirect === undefined">
        <template #icon>
          <pf-empty-state-icon>
            <magnifying-glass-icon />
          </pf-empty-state-icon>
        </template>
        <pf-empty-state-body>
          {{ $t('Nessun dettaglio disponibile') }}
        </pf-empty-state-body>
      </pf-empty-state>

      <pf-tabs v-else-if="services" box>
        <pf-tab v-if="services.pannello" :title="titlePannello" ouia-id="service-tab-pannello" class="pf-v6-u-p-md">
          <template #icon><circle-nodes-icon /></template>

          <service-detail-pannello
            :dominio="services.dominio"
            :pannello="services.pannello"
            :currency="login.currency"
            :with-developers-grants="login.grants.developer"
          />
        </pf-tab>

        <pf-tab v-if="services.posta" :title="$t('Email')" ouia-id="service-tab-email" class="pf-v6-u-p-md">
          <template #icon><envelope-icon /></template>

          <service-detail-pannello
            :dominio="services.dominio"
            :pannello="services.posta"
            :currency="login.currency"
            is-posta
            :with-developers-grants="login.grants.developer"
          />
        </pf-tab>

        <pf-tab v-if="services.pecs" :title="$t('PEC')" ouia-id="service-tab-pec" class="pf-v6-u-p-md">
          <template #icon><envelope-icon /></template>

          <pf-form component="div">
            <form-group :name="$t('Caselle di posta elettronica certificata')">
              <pf-list plain bordered class="pf-v6-u-mt-md" style="max-width: 600px">
                <pf-list-item v-for="pec in services.pecs" :key="pec">
                  <pf-flex justify-content="space-between" style="width: 100%;">
                    <pf-flex-item>
                      <a :href="`mailto:${pec}@${services.dominio}`" target="_blank" rel="noreferer">{{ pec }}@{{ services.dominio }}</a>
                    </pf-flex-item>
                    <pf-flex-item>
                      <pf-button variant="control" small href="https://mail.arterapec.it" target="_blank" rel="noreferer">
                        <template #icon>
                          <arrow-up-right-from-square-icon />
                        </template>
                        {{ $t('Webmail') }}
                      </pf-button>
                    </pf-flex-item>
                  </pf-flex>
                </pf-list-item>
              </pf-list>
            </form-group>
          </pf-form>
        </pf-tab>

        <pf-tab v-if="services.redirect" :title="$t('Redirect')" ouia-id="service-tab-redirect" class="pf-v6-u-p-md">
          <template #icon><arrow-icon /></template>

          <pf-form component="div">
            <form-group :name="$t('Destinazione')" style="max-width: 600px;">
              <form-input
                id="service-redirect"
                :disabled="!login.grants.developer"
                name="redirect"
                v-model="services.redirect"
                @save="save"
              />
            </form-group>
          </pf-form>
        </pf-tab>

        <pf-tab v-if="services.dns || services.is_registrazione_dominio" :title="$t('Gestione dominio e DNS')" ouia-id="service-tab-domain-dns" class="pf-v6-u-p-md">
          <template #icon><domain-icon /></template>

          <template v-if="services.dns">
            <pf-title h="3">{{ $t('Zona DNS') }}</pf-title>
            <dns-records :dominio="services.dominio" />
          </template>
          <pf-divider v-if="services.dns && services.is_registrazione_dominio" class="pf-v6-u-my-lg" />
          <template v-if="services.is_registrazione_dominio">
            <pf-title h="3">{{ $t('Trasferisci altrove il dominio') }}</pf-title>
            <pf-button variant="control" @click="richiediAuthcode(services.dominio)">{{ $t('Richiedi AuthCode') }}</pf-button>
          </template>
        </pf-tab>
      </pf-tabs>

      <pf-alert v-else variant="danger" :title="$t('Errore: Servizi non trovati')" />
    </pf-page-section>
  </page>
</template>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import { computed } from 'vue';
import { pop } from '@common/utils';
import { useRoute } from 'vue-router';
import { useAlertStore } from '@/store/alerts';
import FormInput from '../components/form-input.vue';
import DnsRecords from '../components/dns-records.vue';
import ServiceDetailPannello from '../components/service-detail-pannello.vue';
import { useLoginStore } from '@/store/login';
import { ServiceResource, TicketResource, Resource } from '@/resources';
import { asyncComputed } from '@common/asyncComputed';
import ArrowUpRightFromSquareIcon from '@vue-patternfly/icons/arrow-up-right-from-square-icon';
import CircleNodesIcon from '@vue-patternfly/icons/circle-nodes-icon';
import DomainIcon from '@vue-patternfly/icons/domain-icon';
import ArrowIcon from '@vue-patternfly/icons/arrow-icon';
import EnvelopeIcon from '@vue-patternfly/icons/envelope-icon';
import MagnifyingGlassIcon from '@vue-patternfly/icons/magnifying-glass-icon';

const route = useRoute();
const domain = pop(route.params.domain);

const title = computed(() => domain ? _t('Servizi associati al dominio {0}', [domain]) : 'asd');

const { result: services, state: servicesState } = asyncComputed(async () => {
  if (domain) {
    return await new ServiceResource().get(domain)
  }
  return null;
});

const login = useLoginStore();

const titlePannello = computed(() => services.value?.pannello && services.value?.posta ? 'Web' : _t('Web / Email'));

async function save(value: string, name: string | undefined) {
  if (!name || !services.value) {
    return;
  }

  try {
    await new Resource('services').save(services.value.dominio, {
      [name]: value,
    });
  } catch (ex: any) {
    if (!ex.response || ![401, 404, 422].includes(ex.response.status)) {
      throw ex;
    }
  }
};

async function richiediAuthcode(dominio: string) {
  if (!dominio) {
    return;
  }

  if (!confirm(_t("Sei sicuro di voler richiedere l'AuthCode per il dominio {0}?", [dominio]))) {
    return;
  }

  try {
    await new TicketResource().save({
      titolo: _t("Richiesta AuthCode per il dominio {0}", [dominio]),
      dettaglio: _t("L'utente {0} ha richiesto l'AuthCode per il dominio {1} dalla pagina my hosting dell'area riservata", [login.username, dominio]),
    });
  } catch (ex: any) {
    if (!ex.response || ![401, 404, 422].includes(ex.response.status)) {
      throw ex;
    }
  }

  const alert = useAlertStore();
  alert.add({ title:_t('La richiesta è stata inviata ai nostri tecnici.'), type: 'success', toast: true});
};
</script>
